/**
 * 批量任务API客户端（统一任务管理架构）
 *
 * 架构说明：
 * - 文件管理：上传、解析、查询文件信息
 * - 任务管理：使用统一的 taskAPI（来自 task.ts）
 * - 下载管理：通过 assetId 获取签名URL（来自 library.ts）
 *
 * 使用 DTO + Mapper 模式：
 * - API 返回 DTO 类型
 * - 通过 Mapper 转换为域模型
 * - 组件只使用域模型
 */

import { http } from '@/utils/http'
import { unwrap } from '@/utils/apiClient'
import type { BatchFile } from '@/types/batch'

// ============= 请求类型 =============

export interface StartParseRequest {
  // 解析配置（可选）
}

export interface CreateBatchTaskRequest {
  uids: string[]
  title: string
  description?: string
  voice: string
  audioFormat: string
  rate: number
  pitch: number
  volume: number
  style: string
}

// ============= 响应类型 =============

export interface UploadFileResponse {
  uid: string
  originalName: string
  fileSize: number
  fileType: string
  status: string
  createdAt: string
  totalChars?: number // 如果文件已解析完成，包含字符数
}

export interface ParseStatusResponse {
  uid: string
  status: string
  progress: number
  totalChars?: number
  estimateSeconds?: number
  estimateCredits?: number
  encoding?: string
  baseCharsPerSec?: number
  warnings?: string[]
  errorMessage?: string
}

export interface FileContentResponse {
  uid: string
  rawContent: string
  parsedContent?: string
  cleanedText?: string // 清洗后的文本（用于计费）
  totalChars: number
}

export interface CreateBatchTaskResponse {
  taskId: string
  status: string
  createdAt: string
}

// ============= API客户端类 =============

class BatchAPI {
  private baseURL = '/api/v1/batch'

  // ============= 文件管理 =============

  /**
   * 上传单个文件
   */
  async uploadFile(file: File): Promise<UploadFileResponse> {
    const formData = new FormData()
    formData.append('file', file)

    return unwrap<UploadFileResponse>(() =>
      http.post(`${this.baseURL}/files`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        transform: {
          disableDedupe: true  // 禁用请求去重，允许同时上传多个文件
        }
      })
    )
  }

  /**
   * 获取文件信息
   */
  async getFileInfo(uid: string): Promise<BatchFile> {
    return unwrap<BatchFile>(() =>
      http.get(`${this.baseURL}/files/${uid}`)
    )
  }

  /**
   * 获取文件完整内容（用于前端展示原文/清洗后文本）
   */
  async getFileContent(uid: string): Promise<FileContentResponse> {
    return unwrap<FileContentResponse>(() =>
      http.get(`${this.baseURL}/files/${uid}/content`)
    )
  }

  /**
   * 删除文件
   */
  async deleteFile(uid: string): Promise<void> {
    return unwrap<void>(() =>
      http.delete(`${this.baseURL}/files/${uid}`)
    )
  }

  // ============= 解析管理 =============

  /**
   * 开始解析文件
   */
  async startParse(uid: string, options: StartParseRequest = {}): Promise<ParseStatusResponse> {
    return unwrap<ParseStatusResponse>(() =>
      http.post(`${this.baseURL}/files/${uid}/parse`, options)
    )
  }

  /**
   * 批量获取解析状态
   * @param uids 文件UID列表
   */
  async getBatchParseStatus(uids: string[]): Promise<ParseStatusResponse[]> {
    if (uids.length === 0) {
      return []
    }
    return unwrap<ParseStatusResponse[]>(() =>
      http.get(`${this.baseURL}/files/parse-status`, {
        params: { uids: uids.join(',') }
      })
    )
  }

  // ============= 任务管理 =============

  /**
   * 创建批量任务
   * 注意：任务列表查询、状态查询等使用统一的 taskAPI（来自 task.ts）
   */
  async createBatchTask(data: CreateBatchTaskRequest): Promise<CreateBatchTaskResponse> {
    return unwrap<CreateBatchTaskResponse>(() =>
      http.post(`${this.baseURL}/tasks`, data)
    )
  }

  /**
   * 获取批量任务状态
   * 注意：建议使用统一的 taskAPI.getTaskStatus(taskId) 替代
   */
  async getTaskStatus(taskId: string): Promise<any> {
    return unwrap(() =>
      http.get(`${this.baseURL}/tasks/${taskId}/status`)
    )
  }
}

// 导出单例
export const batchAPI = new BatchAPI()
export default batchAPI
